/**
 * Which connected aircraft nobody has identified, and which one the identify
 * dialog is open for.
 *
 * One store, because the list is read in three places (the fleet rail badge,
 * the Data page, and the prompt that opens unasked) and three pollers of the
 * same endpoint would disagree with each other for a few seconds at a time.
 */

import { create } from 'zustand';

import { getPending, type PendingIdentification } from '@/api/inventory';

interface IdentifyState {
  pending: PendingIdentification[];
  openFor: string | null;
  /** Why the dialog opened: the operator clicked, or it opened by itself. */
  openedBy: 'operator' | 'prompt' | null;
  /** Bumped when the register changes from here, so open screens reload. */
  version: number;
  refresh: () => Promise<void>;
  changed: () => void;
  open: (droneId: string, openedBy?: 'operator' | 'prompt') => void;
  close: () => void;
}

export const useIdentifyStore = create<IdentifyState>((set) => ({
  pending: [],
  openFor: null,
  openedBy: null,
  version: 0,
  refresh: async () => {
    const result = await getPending();
    if (result.ok) set({ pending: result.data.pending });
  },
  changed: () => set((state) => ({ version: state.version + 1 })),
  open: (droneId, openedBy = 'operator') => set({ openFor: droneId, openedBy }),
  close: () => set({ openFor: null, openedBy: null }),
}));

export function usePendingFor(droneId: string): PendingIdentification | undefined {
  return useIdentifyStore((state) => state.pending.find((p) => p.droneId === droneId));
}

/**
 * The aircraft the prompt should open for unasked: one this person connected,
 * not already skipped. Everyone else sees the badge and opens it themselves.
 */
export function nextToAsk(
  pending: PendingIdentification[],
  dismissed: ReadonlySet<string>,
): PendingIdentification | undefined {
  return pending.find((p) => p.mine && !p.skipped && !dismissed.has(p.droneId));
}
